import { useMemo, useState } from 'react';
import { checkDate, checkPrice, type PortalOrder, type PortalOrderLine } from '@provalia/shared';
import { ApiError } from '../../shared/api';
import { ErrorBox, Field, Modal, useToast } from '../../shared/ui';
import { fmtDate, fmtMoney, fmtNumber, fmtPct } from '../../shared/format';
import { api, type DeliveryNoteResult } from '../api';
import { lineLabel, requiredOf } from './status';

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

// ── Rechazar pedido ───────────────────────────────────────────────────────

export function RejectModal({ order, onClose, onDone }: { order: PortalOrder; onClose: () => void; onDone: () => void }): JSX.Element {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const valid = reason.trim().length >= 3;

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/portal/orders/${order.docEntry}/reject`, { reason: reason.trim() });
      toast(`Pedido ${order.docNum} rechazado. Compras ya tiene el aviso con tu motivo.`, 'ok');
      onDone();
      onClose();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Rechazar el pedido ${order.docNum}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button className="btn solid-danger" onClick={() => void submit()} disabled={busy || !valid}>
            {busy ? 'Rechazando…' : 'Rechazar pedido'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p>Compras recibirá un aviso con el motivo y decidirá si modifica el pedido o lo gestiona con otro proveedor.</p>
        <Field label="Motivo del rechazo (obligatorio)" hint={`${reason.trim().length}/254 · mínimo 3 caracteres`}>
          <textarea
            className="textarea"
            value={reason}
            maxLength={254}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej.: no podemos cumplir la fecha requerida; el material no está disponible…"
            autoFocus
          />
        </Field>
        <ErrorBox error={error} />
      </div>
    </Modal>
  );
}

// ── Proponer fechas (apartado 6.4) ────────────────────────────────────────

/** Fecha con la que se abre cada línea: lo último que se sabe de ella. */
function initialDate(l: PortalOrderLine): string {
  switch (l.dateStatus) {
    case 'V':
      return l.proposedDate ?? requiredOf(l) ?? '';
    case 'A':
    case 'C':
      return l.confirmedDate ?? l.proposedDate ?? requiredOf(l) ?? '';
    default:
      // Sin proponer o rechazada: se parte de la que se pidió.
      return l.confirmedDate ?? requiredOf(l) ?? '';
  }
}

export function DatesModal({
  order,
  toleranceDays,
  onClose,
  onDone,
}: {
  order: PortalOrder;
  toleranceDays: number;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const toast = useToast();
  const lines = useMemo(() => order.lines.filter((l) => l.open), [order]);
  const initial = useMemo(() => Object.fromEntries(lines.map((l) => [l.lineNum, initialDate(l)])), [lines]);
  const [values, setValues] = useState<Record<number, string>>(initial);
  // Las líneas que nunca se han confirmado van marcadas: aceptar la fecha pedida también es confirmarla.
  const [include, setInclude] = useState<Record<number, boolean>>(() =>
    Object.fromEntries(lines.map((l) => [l.lineNum, (l.dateStatus === 'P' || l.dateStatus === 'R') && requiredOf(l) !== null])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const toSend = lines.filter((l) => include[l.lineNum] && values[l.lineNum] && requiredOf(l) !== null);
  const checks = toSend.map((l) => checkDate(requiredOf(l) as string, values[l.lineNum] as string, toleranceDays));
  const outside = checks.filter((c) => !c.withinTolerance).length;

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/portal/orders/${order.docEntry}/dates`, {
        lines: toSend.map((l) => ({ lineNum: l.lineNum, date: values[l.lineNum] })),
      });
      const auto = toSend.length - outside;
      const parts: string[] = [];
      if (auto) parts.push(`${plural(auto, 'fecha grabada', 'fechas grabadas')} en SAP`);
      if (outside) parts.push(`${plural(outside, 'fecha pendiente', 'fechas pendientes')} de validar por compras`);
      toast(parts.join(' · '), 'ok');
      onDone();
      onClose();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <Modal
      wide
      title={`Fechas de entrega · pedido ${order.docNum}`}
      onClose={onClose}
      footer={
        <>
          <span className="subtle grow">
            {toSend.length === 0
              ? 'Marca o cambia al menos una fecha'
              : `Se enviarán ${plural(toSend.length, 'línea', 'líneas')}${outside ? ` (${outside} fuera de tolerancia)` : ''}`}
          </span>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button className="btn primary" onClick={() => void submit()} disabled={busy || toSend.length === 0}>
            {busy ? 'Enviando…' : 'Enviar fechas'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">
          Indica cuándo entregarás cada línea. Si la fecha está a ±{plural(toleranceDays, 'día', 'días')} o menos de la requerida se graba
          directamente en el pedido; si no, compras tendrá que validarla.
        </p>
        {lines.length === 0 ? <div className="callout">Este pedido no tiene líneas abiertas.</div> : null}
        <div className="pt-line-forms">
          {lines.map((l) => {
            const required = requiredOf(l);
            const value = values[l.lineNum] ?? '';
            const on = include[l.lineNum] ?? false;
            const check = required && value ? checkDate(required, value, toleranceDays) : null;
            return (
              <div key={l.lineNum} className={`pt-line-form ${on ? 'on' : ''}`}>
                <div className="pt-line-form-head">
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={required === null}
                      onChange={(e) => setInclude({ ...include, [l.lineNum]: e.target.checked })}
                    />
                    <strong>{lineLabel(l.lineNum)}</strong>
                  </label>
                  <span className="mono">{l.itemCode}</span>
                  <span className="subtle pt-clip">{l.description}</span>
                </div>
                {required === null ? (
                  <div className="callout warn">Esta línea no tiene fecha requerida en SAP y no se puede comparar. Avisa a compras.</div>
                ) : (
                  <div className="pt-line-form-body">
                    <div className="subtle">
                      Requerida <strong className="pt-ink">{fmtDate(required)}</strong> · pendientes {fmtNumber(l.openQuantity)}
                    </div>
                    <input
                      className="input pt-date"
                      type="date"
                      value={value}
                      aria-label={`Fecha de entrega de la ${lineLabel(l.lineNum).toLowerCase()}`}
                      onChange={(e) => {
                        setValues({ ...values, [l.lineNum]: e.target.value });
                        setInclude({ ...include, [l.lineNum]: e.target.value !== '' && (e.target.value !== initial[l.lineNum] || l.dateStatus === 'P' || l.dateStatus === 'R') });
                      }}
                    />
                    <div className="pt-check">
                      {!on ? (
                        <span className="subtle">No se envía</span>
                      ) : check === null ? (
                        <span className="subtle">Elige una fecha</span>
                      ) : check.withinTolerance ? (
                        <span className="pt-ok">
                          ✓ Dentro de tolerancia{check.deviationDays ? ` (${signed(check.deviationDays)} días)` : ''}: se grabará directamente
                        </span>
                      ) : (
                        <span className="pt-warn">
                          ⚠ Fuera de tolerancia ({signed(check.deviationDays)} días): compras tendrá que validarla
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <ErrorBox error={error} />
      </div>
    </Modal>
  );
}

const signed = (n: number): string => (n > 0 ? `+${n}` : String(n));

// ── Albarán valorado (apartado 6.5) ───────────────────────────────────────

/** Admite «12,50», «12.50» y «1.234,50». */
export function parsePrice(text: string): number | null {
  let t = text.trim().replace(/\s|€/g, '');
  if (t === '') return null;
  if (t.includes(',') && t.includes('.')) t = t.replace(/\./g, '').replace(',', '.');
  else t = t.replace(',', '.');
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

const priceText = (n: number): string => String(n).replace('.', ',');

export function DeliveryNoteModal({
  order,
  tolerancePct,
  onClose,
  onDone,
}: {
  order: PortalOrder;
  tolerancePct: number;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const toast = useToast();
  const lines = useMemo(() => order.lines.filter((l) => l.open), [order]);
  const [number, setNumber] = useState('');
  const [prices, setPrices] = useState<Record<number, string>>(() => Object.fromEntries(lines.map((l) => [l.lineNum, priceText(l.unitPrice)])));
  const [include, setInclude] = useState<Record<number, boolean>>(() => Object.fromEntries(lines.map((l) => [l.lineNum, true])));
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  /** 502: los precios están en SAP pero el PDF no. No hay que reenviar. */
  const [partial, setPartial] = useState(false);

  const chosen = lines.filter((l) => include[l.lineNum]);
  const invalid = chosen.filter((l) => parsePrice(prices[l.lineNum] ?? '') === null);
  const outside = chosen.filter((l) => {
    const p = parsePrice(prices[l.lineNum] ?? '');
    return p !== null && !checkPrice(l.originalPrice ?? l.unitPrice, p, tolerancePct).withinTolerance;
  }).length;
  const canSend = number.trim() !== '' && chosen.length > 0 && invalid.length === 0 && !fileError;

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    const payload = {
      number: number.trim(),
      lines: chosen.map((l) => ({ lineNum: l.lineNum, price: parsePrice(prices[l.lineNum] ?? '') as number })),
      description: description.trim(),
    };
    const fd = new FormData();
    fd.append('data', JSON.stringify(payload));
    if (file) fd.append('file', file);
    try {
      const { result } = await api.upload<{ result: DeliveryNoteResult }>(`/portal/orders/${order.docEntry}/delivery-note`, fd);
      const parts = [`Albarán ${payload.number} enviado`];
      if (result.accepted.length) parts.push(`${plural(result.accepted.length, 'línea grabada', 'líneas grabadas')} en SAP`);
      if (result.pendingReview.length) parts.push(`${plural(result.pendingReview.length, 'línea pendiente', 'líneas pendientes')} de compras`);
      toast(parts.join(' · '), 'ok');
      onDone();
      onClose();
    } catch (err) {
      setError(err);
      setBusy(false);
      if (err instanceof ApiError && err.status === 502) {
        setPartial(true);
        onDone();
      }
    }
  }

  return (
    <Modal
      wide
      title={`Comunicar albarán · pedido ${order.docNum}`}
      onClose={onClose}
      footer={
        partial ? (
          <button className="btn primary" onClick={onClose}>
            Cerrar
          </button>
        ) : (
          <>
            <span className="subtle grow">
              {chosen.length === 0
                ? 'Marca al menos una línea'
                : `${plural(chosen.length, 'línea', 'líneas')}${outside ? ` · ${outside} con discrepancia` : ' · todo conforme'}`}
            </span>
            <button className="btn" onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button className="btn primary" onClick={() => void submit()} disabled={busy || !canSend}>
              {busy ? 'Enviando…' : 'Enviar albarán'}
            </button>
          </>
        )
      }
    >
      <div className="stack">
        {partial ? (
          <ErrorBox error={error} />
        ) : (
          <>
            <p className="muted">
              Escribe el precio unitario de tu albarán en cada línea. Si coincide con el del pedido o se desvía ±{fmtNumber(tolerancePct, 0)} % o
              menos, se da por bueno y se graba; si no, compras lo revisará.
            </p>
            <Field label="Número de albarán">
              <input className="input pt-big" value={number} maxLength={50} onChange={(e) => setNumber(e.target.value)} autoFocus />
            </Field>
            <div className="pt-line-forms">
              {lines.map((l) => {
                const reference = l.originalPrice ?? l.unitPrice;
                const on = include[l.lineNum] ?? false;
                const raw = prices[l.lineNum] ?? '';
                const price = parsePrice(raw);
                const check = price === null ? null : checkPrice(reference, price, tolerancePct);
                return (
                  <div key={l.lineNum} className={`pt-line-form ${on ? 'on' : ''}`}>
                    <div className="pt-line-form-head">
                      <label className="check">
                        <input type="checkbox" checked={on} onChange={(e) => setInclude({ ...include, [l.lineNum]: e.target.checked })} />
                        <strong>{lineLabel(l.lineNum)}</strong>
                      </label>
                      <span className="mono">{l.itemCode}</span>
                      <span className="subtle pt-clip">{l.description}</span>
                    </div>
                    {on ? (
                      <div className="pt-line-form-body">
                        <div className="subtle">
                          Precio del pedido <strong className="pt-ink">{fmtMoney(reference, l.currency)}</strong> · {fmtNumber(l.openQuantity)} pendientes
                        </div>
                        <input
                          className="input pt-price"
                          inputMode="decimal"
                          value={raw}
                          aria-label={`Precio del albarán, ${lineLabel(l.lineNum).toLowerCase()}`}
                          onChange={(e) => setPrices({ ...prices, [l.lineNum]: e.target.value })}
                        />
                        <div className="pt-check">
                          {check === null ? (
                            <span className="pt-bad">Precio no válido</span>
                          ) : check.withinTolerance ? (
                            <span className="pt-ok">
                              <span aria-hidden="true">👍</span> Conforme{check.exact ? '' : ` (${fmtPct(check.deviationPct)}, dentro de tolerancia)`}
                            </span>
                          ) : (
                            <span className="pt-bad">
                              <span aria-hidden="true">👎</span> Discrepancia
                              {Number.isFinite(check.deviationPct) ? ` (${fmtPct(check.deviationPct)})` : ''}: compras tendrá que validarlo
                            </span>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <Field label="Descripción o comentarios (opcional)">
              <textarea className="textarea" value={description} maxLength={254} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field label="Albarán en PDF (opcional)" hint="Se guardará en los anexos del pedido en SAP." error={fileError}>
              <input
                className="input"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setFile(f);
                  setFileError(f && !(f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')) ? 'Tiene que ser un PDF' : null);
                }}
              />
            </Field>
            <ErrorBox error={error} />
          </>
        )}
      </div>
    </Modal>
  );
}

// ── Revisión de compras (fecha o precio fuera de tolerancia) ──────────────

export function ReviewModal({
  order,
  line,
  kind,
  decision,
  rules,
  onClose,
  onDone,
}: {
  order: PortalOrder;
  line: PortalOrderLine;
  kind: 'date' | 'price';
  decision: 'approve' | 'reject';
  rules: { priceTolerancePct: number; dateToleranceDays: number };
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const toast = useToast();
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const approve = decision === 'approve';
  const what = kind === 'date' ? 'fecha' : 'precio';

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/portal/orders/${order.docEntry}/lines/${line.lineNum}/${kind === 'date' ? 'date-review' : 'price-review'}`, {
        decision,
        comment: comment.trim(),
      });
      toast(
        approve
          ? `${kind === 'date' ? 'Fecha aprobada' : 'Precio aprobado'} y grabado en SAP. Se avisa al proveedor.`
          : `${kind === 'date' ? 'Fecha rechazada' : 'Precio rechazado'}. Se avisa al proveedor.`,
        'ok',
      );
      onDone();
      onClose();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  const required = requiredOf(line);
  const reference = line.originalPrice ?? line.unitPrice;
  return (
    <Modal
      title={`${approve ? 'Aprobar' : 'Rechazar'} ${what} · pedido ${order.docNum}, ${lineLabel(line.lineNum).toLowerCase()}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button className={`btn ${approve ? 'ok' : 'solid-danger'}`} onClick={() => void submit()} disabled={busy}>
            {busy ? 'Guardando…' : approve ? `Aprobar ${what}` : `Rechazar ${what}`}
          </button>
        </>
      }
    >
      <div className="stack">
        <dl className="kv">
          <dt>Artículo</dt>
          <dd>
            <span className="mono">{line.itemCode}</span> · {line.description}
          </dd>
          {kind === 'date' ? (
            <>
              <dt>Requerida</dt>
              <dd>{fmtDate(required)}</dd>
              <dt>Propuesta</dt>
              <dd>
                <strong>{fmtDate(line.proposedDate)}</strong>
                {required && line.proposedDate ? ` (${signed(checkDate(required, line.proposedDate, rules.dateToleranceDays).deviationDays)} días; tolerancia ±${rules.dateToleranceDays})` : ''}
              </dd>
            </>
          ) : (
            <>
              <dt>Precio del pedido</dt>
              <dd>{fmtMoney(reference, line.currency)}</dd>
              <dt>Precio del albarán</dt>
              <dd>
                <strong>{fmtMoney(line.deliveryNotePrice, line.currency)}</strong>
                {line.deliveryNotePrice !== null
                  ? ` (${fmtPct(checkPrice(reference, line.deliveryNotePrice, rules.priceTolerancePct).deviationPct)}; tolerancia ±${fmtNumber(rules.priceTolerancePct, 0)} %)`
                  : ''}
              </dd>
              {line.deliveryNoteNumber ? (
                <>
                  <dt>Albarán</dt>
                  <dd>{line.deliveryNoteNumber}</dd>
                </>
              ) : null}
            </>
          )}
        </dl>
        <p className="muted">
          {approve
            ? kind === 'date'
              ? 'Al aprobarla se graba como fecha comprometida y de entrega de la línea en SAP.'
              : 'Al aprobarlo se graba como nuevo precio de la línea en SAP.'
            : kind === 'date'
              ? 'El proveedor verá la fecha como rechazada y podrá proponer otra.'
              : 'El proveedor verá el precio como rechazado y podrá comunicar otro albarán.'}
        </p>
        <Field label="Comentario para el proveedor (opcional)">
          <textarea className="textarea" value={comment} maxLength={254} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <ErrorBox error={error} />
      </div>
    </Modal>
  );
}
